import React, { useState } from "react";
import { Box, Typography, Card, CardContent, TextField, Button, Alert, IconButton, Stack } from "@mui/material";
import { Close } from "@mui/icons-material";
import { webHelpService } from "../../services/instituteFaqService";

const MAX_FILES = 5;
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT_EXT = new Set(["pdf", "png", "jpg", "jpeg", "webp", "docx"]);

const ContactSupport = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    setFeedback(null);
    if (picked.length > MAX_FILES) {
      setFeedback({ severity: "error", text: `You can attach at most ${MAX_FILES} files.` });
      setFiles([]);
      return;
    }
    for (const f of picked) {
      if (f.size > MAX_BYTES) {
        setFeedback({ severity: "error", text: "Each file must be 8 MB or smaller." });
        setFiles([]);
        return;
      }
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ACCEPT_EXT.has(ext)) {
        setFeedback({ severity: "error", text: "Allowed types: PDF, PNG, JPG, WEBP, DOCX." });
        setFiles([]);
        return;
      }
    }
    setFiles(picked);
  };
  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    const trimmed = (message || "").trim();
    if (trimmed.length < 5) {
      setFeedback({ severity: "error", text: "Please enter at least 5 characters in your message." });
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("message", trimmed);
      fd.append("subject", (subject || "").trim());
      fd.append("source", "web");
      files.forEach((f) => fd.append("attachments", f));
      await webHelpService.contactSupport(fd);
      setFeedback({ severity: "success", text: "Your message was sent to platform support. You will be contacted by email if a reply is needed." });
      setMessage("");
      setSubject("");
      setFiles([]);
    } catch (err) {
      const text =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not send your message. Please try again later.";
      setFeedback({ severity: "error", text });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box sx={{ maxWidth: 640, mx: "auto", p: 2 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Contact support
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send a message to the ERP platform team (super administrators). Include enough detail so we can help quickly.
      </Typography>
      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.text}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent component="form" onSubmit={handleSubmit}>
          <TextField
            label="Subject (optional)"
            fullWidth
            margin="normal"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Message"
            required
            fullWidth
            margin="normal"
            multiline
            minRows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            inputProps={{ maxLength: 5000 }}
          />
          <Button variant="outlined" component="label" sx={{ mt: 1, mr: 1 }} disabled={loading}>
            Attach files
            <input type="file" name="attachments" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.docx" onChange={onFiles} />
          </Button>
          {files.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {files.map((f, index) => (
                <Box
                  key={`${f.name}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {f.name}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => removeFile(index)}
                    disabled={loading}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
          <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading}>
            {loading ? "Sending…" : "Send"}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactSupport;
